import { AlertTriangle } from "lucide-react";

/**
 * Visual-only AI observation flag. Red circle with a warning triangle,
 * with an optional count pill when >1 observations exist. Used across
 * the app anywhere we need to indicate "Gemini flagged this item":
 *
 *   - Dashboard card corners (wrapped by ObservationCornerBadge which
 *     makes it clickable + opens a modal)
 *   - Budget row chips
 *   - Timeline phase header chips
 *
 * Derives the fill color from the highest-severity observation (risk >
 * warn > info), matching the corner-badge convention.
 */
const SEVERITY_BG = {
  risk: "#ef4444",
  warn: "#f59e0b",
  info: "#3b82f6",
};

const SEVERITY_RANK = { risk: 3, warn: 2, info: 1 };

const SIZES = {
  // Compact — inline next to row text
  sm: {
    outer: "w-5 h-5",
    icon: "w-3 h-3",
    badgePos: "-bottom-0.5 -right-0.5",
    badgeSize: "w-3.5 h-3.5 text-[8px]",
    badgeBorder: 1.5,
  },
  // Default — card corner use
  md: {
    outer: "w-6 h-6",
    icon: "w-3.5 h-3.5",
    badgePos: "-bottom-1 -right-1",
    badgeSize: "w-4 h-4 text-[9px]",
    badgeBorder: 2,
  },
};

export default function ObservationFlagIcon({ observations, size = "sm" }) {
  if (!observations || observations.length === 0) return null;

  const worst = observations.reduce((acc, o) =>
    SEVERITY_RANK[o.severity] > SEVERITY_RANK[acc.severity] ? o : acc
  );
  const bg = SEVERITY_BG[worst.severity] ?? SEVERITY_BG.info;
  const count = observations.length;
  const s = SIZES[size] ?? SIZES.sm;

  return (
    <div
      className="relative inline-flex flex-shrink-0"
      title={`${count} AI observation${count > 1 ? "s" : ""}`}
    >
      <div
        className={`${s.outer} rounded-full flex items-center justify-center shadow-sm`}
        style={{ background: bg, color: "white" }}
      >
        <AlertTriangle className={s.icon} strokeWidth={2.5} />
      </div>
      {count > 1 && (
        <span
          className={`absolute ${s.badgePos} ${s.badgeSize} font-bold rounded-full flex items-center justify-center leading-none`}
          style={{
            background: "white",
            color: bg,
            border: `${s.badgeBorder}px solid ${bg}`,
          }}
        >
          {count}
        </span>
      )}
    </div>
  );
}

export { SEVERITY_BG, SEVERITY_RANK };
